import React, { useMemo, useState } from 'react';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { isEqual, startOfDay } from 'date-fns';

import { useLockedFeatures } from '@/providers/LockedFeatures.provider';
import { Button, PaymentMethodLogo, SelectOptionLocked } from '@/components/UI';
import {
  Box,
  Select,
  Tooltip,
  TextField,
  MultiSelect,
  Radio,
  InteracWordmark,
  withStyles,
  BodyText,
  PaymentMethod,
  FieldData,
  PaymentMethodType,
} from 'vopay';

import { PaymentFieldDefault } from './types';
import styles from './TransactionCreationFields.module.scss';
import { useHandlePaymentMethodChange } from './useHandlePaymentMethodChange';
import useFetchClientBankAccounts from '@/hooks/useApi/useFetchClientBankAccounts';
import { useClientData } from '@/providers/ClientData.provider';
import { usePermissions } from '@/providers/Permissions.provider';

const classes = withStyles(styles);

function isToday(dateStr: string): boolean {
  const date = startOfDay(new Date(Number(dateStr)));
  const today = startOfDay(new Date());
  return isEqual(date, today);
}

export function usePaymentSettingsFields(
  formType: 'Pay' | 'Collect',
  country: string,
  paymentMethods: PaymentMethod[],
): FieldData<PaymentFieldDefault>[] {
  type PaymentSelectionMethodOption = {
    label: string;
    value: string;
    featureKey: string;
    visible: boolean;
    renderLabel?: React.ReactNode;
  };
  type BankingPaymentRailOption = {
    label: React.ReactNode;
    value: string;
    featureKey: string;
    visible: boolean;
    isRecurringEnabled: boolean;
  };
  const { PayLinkEnabled } = useLockedFeatures();
  const { t } = useTranslation('translations', { keyPrefix: 'UseTransactionCreationFields' });
  const handlePaymentMethodChange = useHandlePaymentMethodChange();
  const [, , , fetchClientBankAccounts] = useFetchClientBankAccounts({});
  const {
    response: { clientAccountsEnabled, Country },
  } = useClientData();
  const [isNewPaymentMethod, setIsNewPaymentMethod] = useState(false);
  const { permissions } = usePermissions();

  // Helper function to map PaymentMethod types to feature keys
  const getPaymentMethodFeatureKey = (paymentMethod: PaymentMethodType): string => {
    const featureKeyMap: { [key: string]: string } = {
      BankAccount: 'Bank Account',
      Email: formType === 'Pay' ? 'interacEtransfer' : 'interacEtransferMoneyRequest',
      PhoneNumber: formType === 'Pay' ? 'interacEtransfer' : 'interacEtransferMoneyRequest',
      CreditCard: 'Credit Card',
      DebitCard: formType === 'Pay' ? 'OCT' : 'AFT',
      PayPal: 'PayPal Withdraw',
      Venmo: 'Venmo Withdraw',
      GooglePay: 'Google Pay',
      ApplePay: 'Apple Pay',
    };
    return featureKeyMap[paymentMethod] || paymentMethod;
  };

  // Helper function to check if a payment method should be visible
  const isPaymentMethodVisible = (paymentMethod: PaymentMethodType): boolean => {
    if (paymentMethod === 'BankAccount' || paymentMethod === 'Email' || paymentMethod === 'PhoneNumber') {
      return true;
    }
    if (paymentMethod === 'DebitCard') {
      return true;
    }
    if (paymentMethod === 'PayPal' || paymentMethod === 'Venmo') {
      return formType === 'Pay';
    }
    if (paymentMethod === 'CreditCard') {
      return formType === 'Collect';
    }
    return false;
  };

  return useMemo(() => {
    const paymentSelectionMethodOptions: PaymentSelectionMethodOption[] = [
      {
        label: t('paymentSelectionOption.bankAccount'),
        value: 'bank',
        featureKey: getPaymentMethodFeatureKey('BankAccount'),
        visible: true,
      },
      {
        label: 'Interac e-Transfer',
        renderLabel: <InteracWordmark type={formType === 'Pay' ? 'eTransfer' : 'moneyRequest'} />,
        value: 'email',
        featureKey: getPaymentMethodFeatureKey('Email'),
        visible: country === 'CA',
      },
      {
        label: t('paymentSelectionOption.creditCard'),
        value: 'credit',
        featureKey: getPaymentMethodFeatureKey('CreditCard'),
        visible: formType !== 'Pay',
      },
      {
        label: t('paymentSelectionOption.debitCard'),
        value: 'debitcard',
        featureKey: getPaymentMethodFeatureKey('DebitCard'),
        visible: true,
      },
      {
        label: t('paymentSelectionOption.paypal'),
        value: 'paypal',
        featureKey: getPaymentMethodFeatureKey('PayPal'),
        visible: formType === 'Pay',
      },
      {
        label: t('paymentSelectionOption.venmo'),
        value: 'venmo',
        featureKey: getPaymentMethodFeatureKey('Venmo'),
        visible: formType === 'Pay' && country === 'US',
      },
    ];
    const bankingPaymentRailOptions: BankingPaymentRailOption[] = [
      {
        label: t('paymentRail.eft'),
        value: 'eft',
        featureKey: formType === 'Pay' ? 'EFTSendEnabled' : 'EFTCollectEnabled',
        visible: country === 'CA',
        isRecurringEnabled: true,
      },
      {
        label: t('paymentRail.vopayinstant'),
        value: 'vopayinstant',
        featureKey: 'RTRWithdrawEnabled',
        visible: country === 'CA' && formType === 'Pay',
        isRecurringEnabled: false,
      },
      {
        label: t('paymentRail.ach'),
        value: 'ach',
        featureKey: formType === 'Pay' ? 'ACHWithdrawEnabled' : 'ACHFundEnabled',
        visible: country === 'US',
        isRecurringEnabled: true,
      },
      {
        label: t('paymentRail.rtp'),
        value: 'rtp',
        featureKey: 'RTPWithdrawEnabled',
        visible: country === 'US' && formType === 'Pay',
        isRecurringEnabled: false,
      },
      {
        label: t('paymentRail.fedwire'),
        value: 'fedwire',
        featureKey: formType === 'Pay' ? 'FedwireWithdrawEnabled' : 'FedwireFundEnabled',
        visible: country === 'US',
        isRecurringEnabled: false,
      },
    ];

    return [
      // Connection Type Field (from useConnectionTypeFields)
      {
        label: () => t('Fields.paymentMethods.label'),
        fieldName: 'PaymentMethod',
        getInputState: () => 'required',
        showField: (values) => values.Contact !== 'new',
        serializeField: (ctx) => ctx.PaymentMethod,
        renderField: ({ getFormikData, ctx }) => {
          const fieldData = getFormikData<string>({});
          return (
            <Select
              version="2"
              {...fieldData}
              options={paymentMethods.filter(
                (option) =>
                  isPaymentMethodVisible(option.PaymentMethod) &&
                  (option.Currency === ctx.values.Currency || option.Currency === null) &&
                  (!clientAccountsEnabled || option.IsDefault),
              )}
              onChange={async (e, obj) => {
                fieldData.onChange(e);
                if (obj.data) {
                  const newValues = await handlePaymentMethodChange({
                    Token: obj.data.Token,
                    PaymentMethod: obj.data.PaymentMethod,
                    Value: obj.value,
                    Contact: ctx.values.Contact || '',
                  });
                  if (newValues?.values) {
                    Object.entries(newValues.values).forEach(([key, value]) => {
                      ctx.setFieldValue(key, value);
                    });
                  }
                  if (newValues?.touched) {
                    Object.entries(newValues.touched).forEach(([key, value]) => {
                      ctx.setFieldTouched(key, value as boolean);
                    });
                  }

                  setIsNewPaymentMethod(false);
                } else if (obj.value === 'new' || obj.value === 'request') {
                  ctx.setFieldValue('EtransferConnectionType', 'email');
                  ctx.setFieldValue(
                    'ProvideOrRequestPaymentInformation',
                    obj.value === 'request' ? 'request' : 'provide',
                  );
                  ctx.setFieldValue('Answer', '');
                  ctx.setFieldValue('Question', '');
                  ctx.setFieldValue('RecipientEmailAddress', '');
                  ctx.setFieldValue('RecipientPhoneNumber', '');
                  ctx.setFieldValue('FinancialInstitutionNumber', '');
                  ctx.setFieldValue('BranchTransitNumber', '');
                  ctx.setFieldValue('ABARoutingNumber', '');
                  ctx.setFieldValue('AccountNumber', '');
                  ctx.setFieldValue('PaymentSelectionMethod', '');
                  ctx.setFieldValue('BankingPaymentRail', Country === 'CA' ? 'eft' : 'ach');
                  ctx.setFieldValue('AccountSelectionMethod', '');
                  ctx.setFieldValue('InvoiceNumber', '');
                  ctx.setFieldValue('InvoiceFile', '');
                  ctx.setFieldValue('InvoiceFileName', '');
                  ctx.setFieldValue('RecipientMessage', '');

                  ctx.setFieldTouched('Answer', false);
                  ctx.setFieldTouched('Question', false);
                  ctx.setFieldTouched('RecipientEmailAddress', false);
                  ctx.setFieldTouched('RecipientPhoneNumber', false);
                  ctx.setFieldTouched('FinancialInstitutionNumber', false);
                  ctx.setFieldTouched('BranchTransitNumber', false);
                  ctx.setFieldTouched('ABARoutingNumber', false);
                  ctx.setFieldTouched('AccountNumber', false);
                  ctx.setFieldTouched('ProvideOrRequestPaymentInformation', false);
                  ctx.setFieldTouched('PaymentSelectionMethod', false);
                  ctx.setFieldTouched('BankingPaymentRail', false);
                  ctx.setFieldTouched('AccountSelectionMethod', false);
                  ctx.setFieldTouched('InvoiceNumber', false);
                  ctx.setFieldTouched('InvoiceFile', false);
                  ctx.setFieldTouched('InvoiceFileName', false);
                  ctx.setFieldTouched('RecipientMessage', false);
                }
              }}
              renderSelectedValue={(data, { Placeholder, value }) => {
                if (value === 'new') return t('Fields.paymentMethods.provideNewPaymentMethodLabel');
                if (value === 'request') return t('Fields.paymentMethods.requestPaymentMethodButton');

                if (!data) return <Placeholder />;

                return (
                  <div className={styles.paymentDetailsWrapper}>
                    <div className={styles.paymentDetailsItem}>
                      {data.PaymentMethod !== 'GooglePay' && (
                        <PaymentMethodLogo
                          method={data.PaymentMethod}
                          bankId={data.ExtraFields?.BankID}
                          creditCardBrand={data.ExtraFields?.CardBrand}
                          size="24px"
                        />
                      )}
                      <div className={styles.paymentDetails}>
                        <div className={styles.paymentName}>
                          {data.PaymentMethod === 'CreditCard' &&
                            data.ExtraFields.CardBrand !== 'unknown' &&
                            data.ExtraFields.CardBrand}{' '}
                          {data.PaymentMethod === 'DebitCard' &&
                            data.ExtraFields.CardBrand !== 'unknown' &&
                            data.ExtraFields.CardBrand}{' '}
                          {data.PaymentMethod === 'BankAccount' && data.ExtraFields.InstitutionName}{' '}
                          {data.PaymentDetails}
                        </div>
                        <div className={styles.paymentInfo}>
                          {data.PaymentMethod === 'CreditCard' && ` | Exp. ${data.ExtraFields.ExpirationDate}`}
                          {data.PaymentMethod === 'DebitCard' && ` | Exp. ${data.ExtraFields.ExpirationDate}`}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }}
              renderOption={(data, args) => {
                const featureKey = getPaymentMethodFeatureKey(data.PaymentMethod);
                return (
                  <SelectOptionLocked
                    {...args}
                    featureKey={featureKey}
                    onClick={() => {
                      setIsNewPaymentMethod(false);
                    }}
                  >
                    <div className={styles.paymentDetailsItem} style={{ maxWidth: '100%', overflow: 'hidden' }}>
                      {data.PaymentMethod !== 'GooglePay' && (
                        <PaymentMethodLogo
                          method={data.PaymentMethod}
                          bankId={data.ExtraFields?.BankID}
                          creditCardBrand={data.ExtraFields?.CardBrand}
                          size="24px"
                        />
                      )}
                      <div className={styles.paymentDetails}>
                        <div className={styles.paymentName}>
                          {data.PaymentMethod === 'CreditCard' &&
                            data.ExtraFields.CardBrand !== 'unknown' &&
                            data.ExtraFields.CardBrand}{' '}
                          {data.PaymentMethod === 'DebitCard' &&
                            data.ExtraFields.CardBrand !== 'unknown' &&
                            data.ExtraFields.CardBrand}{' '}
                          {data.PaymentMethod === 'BankAccount' && data.ExtraFields.InstitutionName}{' '}
                          {data.PaymentDetails}
                        </div>
                        <div className={styles.paymentInfo}>
                          {data.PaymentMethod === 'CreditCard' && ` | Exp. ${data.ExtraFields.ExpirationDate}`}
                          {data.PaymentMethod === 'DebitCard' && ` | Exp. ${data.ExtraFields.ExpirationDate}`}
                        </div>
                      </div>
                    </div>
                  </SelectOptionLocked>
                );
              }}
              optionValue={(o) => o.Token}
              renderDropdownHeader={
                clientAccountsEnabled
                  ? () => {
                      return (
                        <div className={styles.clientAccountPaymentMethodsHeader}>
                          <div className={styles.clientAccountPaymentMethodsTitle}>
                            {t('Fields.paymentMethods.clientAccountDescription.title')}
                          </div>
                          <BodyText size="sm" weight="md" className={styles.clientAccountPaymentMethodsInfo}>
                            {t('Fields.paymentMethods.clientAccountDescription.info')}{' '}
                            <a
                              href={`/client-accounts/${ctx.values.ClientAccount || ''}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={styles.clientAccountPaymentMethodsLink}
                            >
                              {t('Fields.paymentMethods.clientAccountDescription.link')}
                            </a>{' '}
                          </BodyText>
                        </div>
                      );
                    }
                  : undefined
              }
              renderDropdownFooter={
                !clientAccountsEnabled
                  ? ({ close }) => (
                      <Box flx="row" gapSize="6px">
                        {permissions.AddBeneficiaries && (
                          <Button
                            colorScheme="secondary"
                            size="small"
                            style={{
                              flex: '1',
                            }}
                            onClick={() => {
                              setIsNewPaymentMethod(true);
                              ctx.setFieldValue('PaymentMethod', 'new');

                              close();
                            }}
                            iconLeftProps={{ icon: 'article', fillAmount: 1 }}
                          >
                            {t('Fields.paymentMethods.addNewPaymentMethodButton', {
                              defaultValue: 'Provide Payment Method',
                            })}
                          </Button>
                        )}
                        {PayLinkEnabled && (
                          <Button
                            colorScheme="secondary"
                            size="small"
                            style={{
                              flex: '1',
                            }}
                            onClick={() => {
                              setIsNewPaymentMethod(true);
                              ctx.setFieldValue('PaymentMethod', 'request');

                              close();
                            }}
                            iconLeft="call_received"
                          >
                            {t('Fields.paymentMethods.requestPaymentMethodButton', {
                              defaultValue: 'Request Payment Method',
                            })}
                          </Button>
                        )}
                      </Box>
                    )
                  : undefined
              }
            />
          );
        },

        validate: () => Yup.string().required(),
      },
      {
        label: () => t('Fields.bankingPaymentRail.label'),
        fieldName: 'BankingPaymentRail',
        getInputState: () => 'required',
        showField: (values) =>
          values.PaymentSelectionMethod === 'bank' && values.Contact !== 'new' && !isNewPaymentMethod,
        renderField: ({ ctx, getFormikData }) => {
          const fieldData = getFormikData<string>({});
          return (
            <Select
              version="2"
              placeholder={t('Fields.paymentSelectionMethod.placeholder')}
              options={bankingPaymentRailOptions.filter(
                (option) => option.visible && (ctx.values.Frequency !== 'recurring' || option.isRecurringEnabled),
              )}
              sort={false}
              renderOption={(option, args) => (
                <SelectOptionLocked {...args} featureKey={option.featureKey}>
                  {option.label}
                </SelectOptionLocked>
              )}
              {...fieldData}
              onChange={async (e, obj) => {
                fieldData.onChange(e);
                if (!obj.value) return;
                if (obj.value === 'fedwire') {
                  const bankAccountsResponse = await fetchClientBankAccounts({
                    Token: ctx.values.PaymentMethod,
                  });
                  const bankInfo = Object.values(bankAccountsResponse.BankAccounts).find(
                    (bank) => ctx.values.PaymentMethod === bank.Token,
                  );
                  if (!bankInfo) return;
                  ctx.setValues({
                    ...ctx.values,
                    BankingPaymentRail: obj.value,
                    CompanyName: bankInfo.AccountHolderName,
                  });
                  ctx.setTouched({
                    ...ctx.touched,
                    Answer: false,
                    ClientReferenceNumber: false,
                    Question: false,
                    RecipientEmailAddress: false,
                    RecipientName: false,
                    RecipientPhoneNumber: false,
                  });
                }
              }}
            />
          );
        },
        validate: () =>
          Yup.string().when(['PaymentSelectionMethod'], {
            is: (PaymentSelectionMethod: string) => PaymentSelectionMethod === 'bank',
            then: Yup.string().required(),
            otherwise: Yup.string(),
          }),
      },
      {
        fieldName: 'ProvideOrRequestPaymentInformation',
        label: () => t('Fields.provideOrRequestPaymentInformation.label'),
        getInputState: () => 'optional',
        showField: (values) => values.Contact === 'new' && !!PayLinkEnabled,
        renderField: ({ ctx, getFormikData }) => {
          return (
            <Radio
              {...getFormikData<string>({
                onChange: (e) => {
                  if (ctx.values.ProvideOrRequestPaymentInformation !== e.target.value) {
                    ctx.setFieldValue('ProvideOrRequestPaymentInformation', e.target.value);
                    ctx.setFieldValue('PaymentSelectionMethod', '');
                    ctx.setFieldTouched('PaymentSelectionMethod', false);
                  }
                },
              })}
              variant="outlineShadow"
              noOrdering
              options={{
                provide: t('provideOrRequestInfoOptions.provide'),
                request: t('provideOrRequestInfoOptions.request'),
              }}
            />
          );
        },
      },

      {
        label: () => t('Fields.paymentSelectionMethod.labelRequested'),
        fieldName: 'PaymentSelectionMethod',
        getInputState: () => 'required',
        showField: (values) =>
          !!PayLinkEnabled &&
          values.ProvideOrRequestPaymentInformation === 'request' &&
          (values.Contact === 'new' || values.PaymentMethod === 'new' || values.PaymentMethod === 'request'),
        serializeField: (values) =>
          values.PaymentSelectionMethod?.split(',')
            .filter((paymentMethod) => paymentMethod)
            .join(','),
        tooltip: () => (
          <Tooltip
            information={
              <Box gapSize="4px" flx="col">
                <h3 className={classes('toolTipHeader')}>{t('Fields.paymentSelectionMethod.tooltipHeader')}</h3>
                <div>{t('Fields.paymentSelectionMethod.tooltipInfo')}</div>
              </Box>
            }
            position="top"
            width="394px"
          />
        ),
        renderField: ({ ctx, getFormikData }) => {
          const fieldData = getFormikData<string[], string>({
            formatValue: (value) => (value ? value.split(',') : []),
          });

          return (
            <MultiSelect
              placeholder={t('Fields.paymentSelectionMethod.placeholder')}
              options={
                isToday(ctx.values.StartDate || '') && ctx.values.ScheduleType === 'single'
                  ? paymentSelectionMethodOptions.filter((option) => option.visible)
                  : paymentSelectionMethodOptions.filter((option) => option.visible && option.value === 'bank')
              }
              renderOption={(option, args) => (
                <SelectOptionLocked {...args} featureKey={option.featureKey}>
                  {option.renderLabel || option.label}
                </SelectOptionLocked>
              )}
              sort={false}
              {...fieldData}
            />
          );
        },

        validate: () => Yup.mixed().required(),
      },

      {
        label: () => t('Fields.paymentSelectionMethod.label'),
        fieldName: 'PaymentSelectionMethod',
        getInputState: () => 'required',
        showField: (values) =>
          values.ProvideOrRequestPaymentInformation === 'provide' &&
          (values.Contact === 'new' || values.PaymentMethod === 'new' || values.PaymentMethod === 'request'),
        helpText: (values: PaymentFieldDefault) => {
          if (
            (values.PaymentSelectionMethod === 'credit' || values.PaymentSelectionMethod === 'debitcard') &&
            values.PaymentMethod !== 'request'
          ) {
            return t('Fields.paymentSelectionMethod.helpText');
          }
          if (values.PaymentSelectionMethod === 'paypal' && values.PaymentMethod !== 'request') {
            return t('Fields.paymentSelectionMethod.helpTextPayPal');
          }
          if (values.PaymentSelectionMethod === 'venmo' && values.PaymentMethod !== 'request') {
            return t('Fields.paymentSelectionMethod.helpTextVenmo');
          }
          return null;
        },
        renderField: ({ getFormikData }) => {
          const fieldData = getFormikData<string>({});
          return (
            <Select
              version="2"
              {...fieldData}
              options={paymentSelectionMethodOptions.filter((option) => option.visible)}
              renderOption={(option, args) => (
                <SelectOptionLocked {...args} featureKey={option.featureKey}>
                  {option.renderLabel || option.label}
                </SelectOptionLocked>
              )}
            />
          );
        },
        validate: () => Yup.string().required(),
      },
      {
        label: () => t('Fields.bankingPaymentRail.label'),
        fieldName: 'BankingPaymentRail',
        getInputState: () => 'required',
        showField: (values) =>
          !!values.PaymentSelectionMethod?.includes('bank') &&
          values.ProvideOrRequestPaymentInformation === 'provide' &&
          (values.Contact === 'new' || values.PaymentMethod === 'new' || values.PaymentMethod === 'request'),
        serializeField: (values) => values.BankingPaymentRail,
        renderField: ({ ctx, getFormikData }) => {
          const fieldData = getFormikData<string>({});
          return (
            <Select
              version="2"
              placeholder={t('Fields.bankingPaymentRail.placeholder')}
              options={bankingPaymentRailOptions.filter(
                (option) => option.visible && (ctx.values.Frequency !== 'recurring' || option.isRecurringEnabled),
              )}
              sort={false}
              renderOption={(option, args) => (
                <SelectOptionLocked {...args} featureKey={option.featureKey}>
                  {option.label}
                </SelectOptionLocked>
              )}
              {...fieldData}
            />
          );
        },
        validate: (ctx) => (ctx.values.PaymentSelectionMethod?.includes('bank') ? Yup.mixed().required() : Yup.mixed()),
      },
      {
        label: () => t('Fields.cancellationPeriod.label'),
        fieldName: 'CancellationPeriod',
        getInputState: () => 'optional',
        showField: (ctx) =>
          ctx.PaymentSelectionMethod === 'email' && ctx.ProvideOrRequestPaymentInformation === 'provide',
        serializeField: (ctx) => ctx.CancellationPeriod,
        renderField: ({ getFormikData }) => {
          const fieldData = getFormikData<string>({});
          return <TextField {...fieldData} variant="outlineShadow" preset="number" placeholder="" />;
        },
        validate: () =>
          Yup.string().test('cancellation-period-validation', t('Fields.cancellationPeriod.validation'), (value) => {
            const periodValidation = Number(value) > 30;
            return !periodValidation;
          }),
      },
      {
        label: () => t('Fields.receiverPhoneNumber.label'),
        fieldName: 'ReceiverPhoneNumber',
        getInputState: (ctx) => (ctx.values.PaymentMethodDisplayOptions === 'New' ? 'optional' : 'required'),
        showField: (values) =>
          !!values.PaymentMethodDisplayOptions &&
          values.PaymentMethodDisplayOptions !== 'New' &&
          values.ProvideOrRequestPaymentInformation === 'request',
        serializeField: (values) => values.ReceiverPhoneNumber,
        tooltip: () => (
          <Tooltip
            information={<div>{t('Fields.receiverPhoneNumber.tooltipInfo')}</div>}
            position="top"
            width="298px"
          />
        ),
        renderField: ({ getFormikData }) => {
          const fieldData = getFormikData<string>({});
          return <TextField variant="outlineShadow" placeholder="" {...fieldData} />;
        },
        validate: (ctx) => (ctx.values.PaymentMethodDisplayOptions !== 'New' ? Yup.mixed().required() : Yup.mixed()),
      },
    ];
  }, [formType, country, paymentMethods, isNewPaymentMethod]);
}
